import { useState, useEffect } from "react";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import {
    Tabs,
    Tab,
    CircularProgress,
    Button,
    Grid,
    Box,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { request } from "@/Api";
import ApiAutocomplete from "@/common/ApiAutocomplete";
import { CustomBox } from "@/common/StyledComponents";
import { Subtitle, SectionTitle, ErrorMessage } from "@/common/typography";
import { selectBidPopupStatus, closeBidPopup } from "@/nav/navSlice";
import { FilesStore } from "@/common/FilesUploader";
import useBidImport from "./useBidImport";
import useCustomBidImport from "./useCustomBidImport";

export default function BidPopup() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const popupStatus = useSelector(selectBidPopupStatus);

    const bidPopupOpenType = popupStatus.type;

    const [projectId, setProjectId] = useState(popupStatus.projectId);
    const [popupState, setPopupState] = useState("templates");
    const [template, setTemplate] = useState(null);
    const [error, setError] = useState(null);
    const [isSmartImportTabActive, setIsSmartImportTabActive] = useState(false);

    const createBidFromTemplate = () => {
        setPopupState("loading");

        request
            .post("/bids/copy", {
                bid_name: template.name,
                project_id: projectId,
                bid_id: template.bid_id,
                is_change_order: bidPopupOpenType == "Change Order",
                public: template.public,
            })
            .catch(() => {
                setError(
                    "There was a problem creating a bid sheet. Please try again or contact support.",
                );
            })
            .then((response) => {
                dispatch(closeBidPopup());
                navigate(`/bids/${response.data.id}`);
            });
    };

    useEffect(() => {
        if (!template) {
            return;
        }

        if (projectId) {
            createBidFromTemplate();
        } else {
            setPopupState("projects");
        }
    }, [template]);

    return (
        <CustomBox
            width={isMobile ? "90%" : "80%"}
            padding={isMobile ? "24px 12px" : "24px"}
            height={isMobile ? "90vh" : "80vh"}
            sx={{ background: theme.palette.background.default, outline: 0 }}
        >
            {popupState == "loading" ? (
                <Grid
                    container
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    sx={{ height: "100%" }}
                >
                    {error ? (
                        <Grid item>
                            <ErrorMessage>{error}</ErrorMessage>
                        </Grid>
                    ) : (
                        <>
                            <Grid item>
                                <CircularProgress />
                            </Grid>
                            <Grid item>
                                <Subtitle>Creating bid sheet...</Subtitle>
                            </Grid>
                        </>
                    )}
                </Grid>
            ) : popupState == "templates" ? (
                <BidPopupTemplates
                    theme={theme}
                    bidPopupOpenType={bidPopupOpenType}
                    setTemplate={setTemplate}
                    projectId={projectId}
                    closeBidPopup={() => dispatch(closeBidPopup())}
                    setIsSmartImportTabActive={setIsSmartImportTabActive}
                />
            ) : popupState == "projects" ? (
                <>
                    <SectionTitle margin="0 24px 0 0">
                        Select Project
                    </SectionTitle>
                    <ApiAutocomplete
                        value={null}
                        apiRoute={"projects"}
                        fields={["name"]}
                        onChange={(event, newValue) =>
                            setProjectId(newValue.id)
                        }
                        label="Project"
                        simple={true}
                        fullOptionData
                    />
                </>
            ) : null}

            <Grid
                container
                xs={12}
                sm={6}
                md={4}
                columnSpacing={2}
                justifyContent="flex-end"
                sx={{
                    position: "absolute",
                    bottom: "24px",
                    right: "24px",
                    zIndex: 100,
                }}
            >
                {!isSmartImportTabActive &&
                    (popupState != "loading" || error) && (
                        <Grid item>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => dispatch(closeBidPopup())}
                            >
                                Cancel
                            </Button>
                        </Grid>
                    )}
                {popupState == "projects" && (
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={createBidFromTemplate}
                            data-testid="create-bid-submit"
                        >
                            Create
                        </Button>
                    </Grid>
                )}
            </Grid>
        </CustomBox>
    );
}

const BidPopupTemplates = ({
    theme,
    bidPopupOpenType,
    setTemplate,
    projectId,
    closeBidPopup,
    setIsSmartImportTabActive,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [bidTemplates, setBidTemplates] = useState({});
    const [tabValue, setTabValue] = useState(0);
    const importTabIndex = Object.keys(bidTemplates).length;
    const smartImportTabIndex = Object.keys(bidTemplates).length + 1;
    const isImportTab = tabValue === importTabIndex;
    const isSmartImportTab = tabValue === smartImportTabIndex;

    const { importBox, importBidFile } = useBidImport({
        projectId,
        onFilesTransfer: (files) => {
            importBidFile(files[0]);
        },
        onImportBidSuccess: (bid) => {
            closeBidPopup();
            navigate(`/bids/${bid.id}`);
        },
    });

    const { importUI: customImportUI } = useCustomBidImport({
        projectId,
        onImportBidSuccess: (bid) => {
            closeBidPopup();
            navigate(`/bids/${bid.id}`);
        },
        onCancel: closeBidPopup,
    });

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const getBidTemplates = () => {
        request
            .get("/bid-templates", { params: { set: "active" } })
            .then((response) => {
                const bid_templates_raw = response.data;
                setBidTemplates(
                    bid_templates_raw.reduce((acc, template) => {
                        const tab = template.public
                            ? "Public"
                            : template.person_id
                              ? "My Templates"
                              : "Workspace";
                        const category = template.category || "Custom";
                        acc[tab] = acc[tab] || {};
                        acc[tab][category] = acc[tab][category] || [];
                        acc[tab][category].push(template);
                        return acc;
                    }, {}),
                );
            });
    };

    useEffect(() => {
        getBidTemplates();
    }, []);

    useEffect(() => {
        if (setIsSmartImportTabActive)
            setIsSmartImportTabActive(
                tabValue === Object.keys(bidTemplates).length + 1,
            );
    }, [bidTemplates, tabValue, setIsSmartImportTabActive]);

    return (
        <div
            style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                paddingBottom: isSmartImportTab ? "0px" : "60px",
                boxSizing: "border-box",
            }}
        >
            {!isSmartImportTab && (
                <>
                    <SectionTitle>Choose a Template</SectionTitle>
                    <Subtitle>{`to create a ${bidPopupOpenType}`}</Subtitle>
                </>
            )}

            <Tabs
                value={tabValue}
                onChange={handleTabChange}
                sx={{ marginTop: "16px" }}
            >
                {Object.entries(bidTemplates).map((type) => {
                    return <Tab label={type[0]} key={type[0]} />;
                })}
                <Tab label="Import Default" key="import" />
                <Tab label="Smart Import" key="smart_import" />
            </Tabs>

            {Object.entries(bidTemplates).map((type, index) => (
                <div
                    hidden={index != tabValue}
                    key={index}
                    style={{ overflow: "auto" }}
                >
                    {Object.entries(type[1]).map((category) => (
                        <div key={category[0]}>
                            <p>{category[0]}</p>
                            <Grid container spacing={2}>
                                {category[1].map((template) => (
                                    <Grid item key={template.id}>
                                        <Box
                                            borderRadius={5}
                                            sx={{
                                                background:
                                                    theme.palette.background
                                                        .paper,
                                                height: 150,
                                                width: 170,
                                                cursor: "pointer",
                                            }}
                                            onClick={() =>
                                                setTemplate(template)
                                            }
                                            data-testid={`bid-template-${template.id}`}
                                        >
                                            <div style={{ padding: 30 }}>
                                                <ArrowCircleRightIcon fontSize="large" />
                                                <br />
                                                {template.name}
                                                <br />
                                            </div>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </div>
                    ))}
                </div>
            ))}
            {isImportTab && (
                <div style={{ flexGrow: 1, minHeight: 0 }}>{importBox}</div>
            )}
            {isSmartImportTab && (
                <div
                    style={{
                        flexGrow: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {customImportUI}
                </div>
            )}
        </div>
    );
};
